/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertBanner,
  Box,
  Button,
  FooterActionBar,
  InputField,
  MenuInput,
  Typography,
} from '@saviynt/design-system';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { type RootState } from '../../../../../services/types';
import { isEmpty, useInvertedLocalize } from '../../../../../utils';
import { type Option } from '../../../data-layer/constants';
import { type IDPConfigKosmos, type Provider } from '../../../data-layer/types';
import { usePostIDProvider, useUpdateIDPProvider } from '../../../hooks';
import { Header } from '../../common';

import invertedMessages from './messages';

import './OneKosmos.css';

interface IDPSetupPageProps {
  isOpen: boolean;
  onClose: () => void;
  provider: Provider | undefined;
  currentProvider: IDPConfigKosmos | undefined;
  refetchIDPProviders: () => void;
  setShowSuccessBanner: (val: boolean) => void;
  currentVendor: Provider | undefined;
}

interface VendorConfig {
  DVCID?: string;
  TENANT_DNS?: string;
  COMMUNITY_NAME?: string;
  DOCUMENT_TYPE?: string;
  LICENSE?: string; // Add LICENSE property
}

function KosmosSettings({
  isOpen,
  onClose,
  provider,
  currentProvider,
  refetchIDPProviders,
  setShowSuccessBanner,
  currentVendor,
}: IDPSetupPageProps) {
  const [document, setDocument] = useState(
    currentProvider?.config?.DOCUMENT_TYPE
  );
  const [license, setLicense] = useState(currentProvider?.config?.LICENSE);
  const [tenantDNS, setTenantDNS] = useState(
    currentProvider?.config?.TENANT_DNS
  );
  const [dvcID, setDvcID] = useState(currentProvider?.config?.DVCID);
  const [showCriticalBanner, setShowCriticalBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLicenseVisible, setIsLicenseVisible] = useState(false);
  const [community, setCommunity] = useState(
    currentProvider?.config?.COMMUNITY_NAME
  );
  const [isErrorAlertVisible, setIsErrorAlertVisible] = useState(false);
  const { isMenuPinned } = useSelector((state: RootState) => state.user);

  const l = useInvertedLocalize(invertedMessages);

  useEffect(() => {
    if (currentProvider) {
      setLicense(currentProvider.config?.LICENSE);
      setTenantDNS(currentProvider.config?.TENANT_DNS);
      setDvcID(currentProvider.config?.DVCID);
      setDocument(currentProvider.config?.DOCUMENT_TYPE);
      setCommunity(currentProvider.config?.COMMUNITY_NAME);
    }
  }, [currentProvider]);

  const { mutate: saveIDPConfigKosmos } = usePostIDProvider();
  const { mutate: updateIDPConfigKosmos, isPending: isUpdateInProgress } =
    useUpdateIDPProvider();

  const handleSave = () => {
    if (provider && !isEmpty(currentProvider)) {
      const vendorConfig: {
        vendorName: string;
        status: number;
        config: VendorConfig;
      } = {
        vendorName: provider.id,
        status: 0,
        config: {
          DVCID: dvcID,
          TENANT_DNS: tenantDNS,
          COMMUNITY_NAME: community,
          DOCUMENT_TYPE: document,
        },
      };

      if (!isEmpty(currentProvider)) {
        vendorConfig.status = currentProvider?.status ?? 0;
      }

      if (
        !isEmpty(currentProvider) &&
        license !== currentProvider?.config?.LICENSE &&
        license
      ) {
        vendorConfig.config.LICENSE = license;
      }

      if (!isEmpty(currentProvider) && license?.trim() === '') {
        setIsErrorAlertVisible(true);

        return;
      }

      setIsErrorAlertVisible(false);

      updateIDPConfigKosmos(vendorConfig as IDPConfigKosmos, {
        onSuccess(response) {
          if (response.errorMessage) {
            setErrorMessage(response.errorMessage);
            setShowCriticalBanner(true);
          } else {
            onClose();
            refetchIDPProviders();
            setShowSuccessBanner(true);
          }
        },
      });
    } else if (currentVendor) {
      const vendorConfig = {
        vendorName: currentVendor.id,
        status: 0,
        config: {
          LICENSE: license,
          DVCID: dvcID,
          TENANT_DNS: tenantDNS,
          COMMUNITY_NAME: community,
          DOCUMENT_TYPE: document,
        },
      };

      saveIDPConfigKosmos(vendorConfig as IDPConfigKosmos, {
        onSuccess(response) {
          if (response.errorMessage) {
            setErrorMessage(response.errorMessage);
            setShowCriticalBanner(true);
          } else {
            onClose();
            refetchIDPProviders();
          }
        },
      });
    }
  };

  const licenseField = useMemo(
    () => (
      <InputField
        name='license'
        type={isLicenseVisible ? 'text' : 'password'}
        suffixButtonType='password'
        suffixIsToggle={isLicenseVisible}
        suffixOnClick={() => setIsLicenseVisible((prev) => !prev)}
        label={
          <Typography kind='label' htmlFor='License'>
            {l('License key')}
          </Typography>
        }
        placeholder={l('Enter License key')}
        value={license}
        setValue={setLicense}
        isCritical={isErrorAlertVisible}
        setIsCritical={setIsErrorAlertVisible}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setLicense(e.target.value)
        }
        className='Ext-KosmosSettings-content-inputField'
        isRequired
        maxLength={100}
        minRequiredChars={3}
      />
    ),
    [
      license,
      setLicense,
      l,
      isLicenseVisible,
      isErrorAlertVisible,
      setIsErrorAlertVisible,
    ]
  );

  const dvcIDField = useMemo(
    () => (
      <InputField
        name='dvcID'
        label={
          <Typography kind='label' htmlFor='dvcID'>
            {l('Verification Config ID')}
          </Typography>
        }
        placeholder={l('Enter Verification Config ID')}
        value={dvcID}
        setValue={setDvcID}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setDvcID(e.target.value)
        }
        className='Ext-KosmosSettings-content-inputField'
        isRequired
        maxLength={100}
        minRequiredChars={3}
      />
    ),
    [dvcID, setDvcID, l]
  );

  const tenantDNSField = useMemo(
    () => (
      <InputField
        name='tenantDNS'
        label={
          <Typography kind='label' htmlFor='TenantDNS'>
            {l('Tenant DNS')}
          </Typography>
        }
        placeholder={l('Enter Tenant DNS')}
        value={tenantDNS}
        setValue={setTenantDNS}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTenantDNS(e.target.value)
        }
        className='Ext-KosmosSettings-content-inputField'
        isRequired
        maxLength={100}
        minRequiredChars={3}
        shouldDisallowBrackets={false}
      />
    ),
    [tenantDNS, setTenantDNS, l]
  );

  const communityField = useMemo(
    () => (
      <InputField
        name='community'
        label={
          <Typography kind='label' htmlFor='Community Name'>
            {l('Community Name')}
          </Typography>
        }
        placeholder={l('Enter Community Name')}
        value={community}
        setValue={setCommunity}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setCommunity(e.target.value)
        }
        className='Ext-KosmosSettings-content-inputField'
        maxLength={255}
        isRequired
        minRequiredChars={3}
      />
    ),
    [community, setCommunity, l]
  );

  const documentField = useMemo(() => {
    const initialDocumentOptions = [
      { label: l('Passport'), value: 'ppt_object' },
      { label: l('Driver’s License'), value: 'dl_object' },
      { label: l('Any Document'), value: 'idcard_object' },
    ];

    return (
      <Box>
        <Typography kind='label' htmlFor='Environment'>
          {l('Which document you would like to verify?')}
        </Typography>
        <MenuInput
          options={initialDocumentOptions.map((option) => ({
            label: option.label,
            value: option.value,
            isChecked: option.value === document,
          }))}
          onChange={(selectedOption: Option) =>
            setDocument(selectedOption.value)
          }
          placeholder={l('Select Document')}
          className='Ext-IDDataWebSettings-content-menuInput'
          isSearchDisabled
        />
      </Box>
    );
  }, [l, document]);

  const isSaveDisabled =
    isEmpty(license) ||
    isEmpty(tenantDNS) ||
    isEmpty(dvcID) ||
    isEmpty(document) ||
    isEmpty(community) ||
    isUpdateInProgress;

  if (!isOpen) {
    return null;
  }

  const resetBanners = () => {
    setShowCriticalBanner(false);
  };

  return (
    <div
      className='Ext-KosmosSettings'
      role='dialog'
      aria-labelledby='setUpPageTitle'
      aria-hidden={!isOpen}>
      <div className='Ext-KosmosSettings-contentWrapper'>
        {provider ? (
          <Header
            currentProvider={currentProvider}
            provider={provider}
            resetBanners={resetBanners}
          />
        ) : (
          ''
        )}
        <AlertBanner
          colorTheme='Warning'
          kind='low'
          title={l('Invalid input')}
          description={l('Please enter valid input.')}
          isVisible={!isSaveDisabled && isErrorAlertVisible}
          onCancel={() => setIsErrorAlertVisible(false)}
          shouldAutoDismiss
          isFloating
          position={{
            type: 'absolute',
            top: '8rem',
            left: '7rem',
          }}
        />
        <AlertBanner
          colorTheme='Critical'
          title={errorMessage}
          description=''
          isVisible={showCriticalBanner}
          onCancel={() => setShowCriticalBanner(false)}
          isFloating
          position={{
            type: 'absolute',
            top: '8rem',
            left: '7rem',
          }}
          shouldAutoDismiss
          isColonVisible={false}
        />
        <div className='Ext-KosmosSettings-content'>
          {tenantDNSField}
          {communityField}
          {licenseField}
          {dvcIDField}
          {documentField}
        </div>
      </div>
      <div
        className={classNames(
          'Ext-KosmosSettings-footer',
          isMenuPinned && 'Ext-KosmosSettings-footer--isMenuPinned'
        )}>
        <FooterActionBar>
          <Button kind='outlined' onClick={onClose}>
            {l('Back')}
          </Button>
          <Button
            kind='filled'
            onClick={handleSave}
            isLoading={isUpdateInProgress}
            loaderText='Saving'
            isDisabled={isSaveDisabled}>
            {l('Save')}
          </Button>
        </FooterActionBar>
      </div>
    </div>
  );
}

export default KosmosSettings;
